import styled from '@emotion/styled'
import React, { useEffect } from 'react'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import Image from 'next/image'
import fitty from 'fitty'
import { useLocale } from '@/hooks/useLocale'

type Props = {
  targetId: string
  templateColor: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  playerId: string
  name: string
  sex: Sex
  platform: Array<Platform>
  voiceChat: Array<VoiceChat>
  voiceChatDetail: string
  fontColor: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  hitokoto: string
  weapons: number[]
  previewImage?: string | null
  favoriteMonster: string
  hunterRank: string
}

export const MonsterHunterWilds1: React.FC<Props> = ({
  playerId,
  name,
  sex,
  platform,
  fontColor,
  templateColor,
  voiceChat,
  voiceChatDetail,
  playTimeWeekDayBegin,
  playTimeWeekDayEnd,
  playTimeWeekEndBegin,
  playTimeWeekEndEnd,
  hitokoto,
  weapons,
  targetId,
  previewImage,
  favoriteMonster,
  hunterRank,
}) => {
  const { t } = useLocale()
  useEffect(() => {
    fitty('#name', {
      maxSize: 20,
      minSize: 12,
    })
    fitty('#age', {
      maxSize: 20,
      minSize: 12,
    })
    fitty('#playerId', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#platform', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#voiceChat', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#playStyle', {
      maxSize: 20,
      minSize: 12,
    })
    fitty('#favoriteMonster', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#hunterRank', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#hitokoto', {
      maxSize: 20,
      minSize: 14,
    })
  }, [])

  return (
    <ProfileContainer>
      <ProfileInner id={targetId} fontColor={fontColor}>
        <Image
          width="2000"
          height="1500"
          priority={true}
          unoptimized={true}
          src={`/static/images/profiles/monsterHunterWilds1/${templateColor}.jpeg`}
          alt={`モンスターハンターワイルズ自己紹介カード ${templateColor}`}
        />
        {previewImage && <PreviewImage src={previewImage} alt="Preview" />}
        <Name>
          <span id="name">{name}</span>
        </Name>
        {sex === 0 && <Sex0 />}
        {sex === 1 && <Sex1 />}
        <PlatformText>
          <span id="platform">
            {platform.includes(0) && ' PC '}
            {platform.includes(1) && ' PlayStation '}
            {platform.includes(2) && ' Xbox '}
          </span>
        </PlatformText>
        <VoiceChatText>
          <span id="voiceChat">
            {voiceChat.includes(0) && ' Skype '}
            {voiceChat.includes(1) && ' LINE '}
            {voiceChat.includes(2) && ' PSVC '}
            {voiceChat.includes(3) && ' Discord '}
          </span>
        </VoiceChatText>
        {/* Weapons */}
        {weapons.includes(0) && <Weapon0 />} {/* 片手剣 */}
        {weapons.includes(1) && <Weapon1 />} {/* 双剣 */}
        {weapons.includes(2) && <Weapon2 />} {/* ハンマー */}
        {weapons.includes(3) && <Weapon3 />} {/* ランス */}
        {weapons.includes(4) && <Weapon4 />} {/* ガンランス */}
        {weapons.includes(5) && <Weapon5 />} {/* 太刀 */}
        {weapons.includes(6) && <Weapon6 />} {/* スラッシュアックス */}
        {weapons.includes(7) && <Weapon7 />} {/* チャージアックス */}
        {weapons.includes(8) && <Weapon8 />} {/* 操虫棍 */}
        {weapons.includes(9) && <Weapon9 />} {/* ライトボウガン */}
        {weapons.includes(10) && <Weapon10 />} {/* ヘビィボウガン */}
        {weapons.includes(11) && <Weapon11 />} {/* 弓 */}
        {weapons.includes(12) && <Weapon12 />} {/* 狩猟笛 */}
        {weapons.includes(13) && <Weapon13 />} {/* 大剣 */}
        <TimeWeekDay>
          {t.平日} {playTimeWeekDayBegin}〜{playTimeWeekDayEnd}
          {t.時終了}
        </TimeWeekDay>
        <TimeWeekEnd>
          {t.土日} {playTimeWeekEndBegin}〜{playTimeWeekEndEnd}
          {t.時終了}
        </TimeWeekEnd>
        <FavoriteMonsterContainer>
          <FavoriteMonsterText>
            <span id="favoriteMonster">{favoriteMonster}</span>
          </FavoriteMonsterText>
        </FavoriteMonsterContainer>
        <HunterRankText>
          <span id="hunterRank">{hunterRank}</span>
        </HunterRankText>
      </ProfileInner>
    </ProfileContainer>
  )
}

const ProfileContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: #4c4c4c;
`

const ProfileInner = styled.div<{ fontColor: string }>`
  position: relative;
  line-height: 0;

  * {
    line-height: 1;
    font-family: 'ap';
    color: ${({ fontColor }) => fontColor};
    border-color: ${({ fontColor }) => fontColor};
  }
`

const Name = styled.div`
  position: absolute;
  top: 54%;
  left: 4%;
  width: 40%;
  height: 20%;
  text-align: center;
  justify-content: center;
  word-break: break-all;
  display: flex;
  span {
    letter-spacing: -1px;
    font-weight: bold;
    line-height: 11px;
  }
`

const SexBase = styled.div`
  position: absolute;
  top: 56%;
  left: 27.7%;
  border-radius: 50%;
  border-style: solid;
  border-width: 2px;
  width: 16px;
  height: 16px;
`

const Sex0 = styled(SexBase)`
  left: 31.6%;
`
const Sex1 = styled(SexBase)`
  left: 27.7%;
`

const PlatformText = styled.div`
  position: absolute;
  top: 54%;
  left: 37.5%;
  width: 30%;
  text-align: center;
`

const VoiceChatText = styled.div`
  position: absolute;
  top: 70.5%;
  left: 64%;
  width: 30%;
  text-align: center;
`

const TimeBase = styled.div`
  position: absolute;
  top: 69%;
  left: 5.5%;
  text-align: center;
  width: 36%;
  font-size: 13px;
  font-weight: bold;
  word-break: break-all;
  line-height: 1.2;
`

const TimeWeekDay = styled(TimeBase)``
const TimeWeekEnd = styled(TimeBase)`
  top: 73%;
`

const WeaponBase = styled.div`
  position: absolute;
  top: 85%;
  left: 10%;
  width: 32px;
  height: 32px;
  border-style: solid;
  border-width: 2px;
  border-radius: 50%;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: transparent;
`

// First Row
export const Weapon0 = styled(WeaponBase)``
export const Weapon1 = styled(WeaponBase)`
  left: 15%;
`
export const Weapon2 = styled(WeaponBase)`
  left: 20.5%;
`
export const Weapon3 = styled(WeaponBase)`
  left: 27%;
`

export const Weapon4 = styled(WeaponBase)`
  left: 32.5%;
`
export const Weapon5 = styled(WeaponBase)`
  left: 37.5%;
`
export const Weapon6 = styled(WeaponBase)`
  left: 43.5%;
`
export const Weapon7 = styled(WeaponBase)`
  left: 49%;
`

export const Weapon8 = styled(WeaponBase)`
  left: 54.5%;
`
export const Weapon9 = styled(WeaponBase)`
  left: 61%;
`
export const Weapon10 = styled(WeaponBase)`
  left: 66.5%;
`
export const Weapon11 = styled(WeaponBase)`
  left: 72.5%;
`

export const Weapon12 = styled(WeaponBase)`
  left: 78%;
`
export const Weapon13 = styled(WeaponBase)`
  left: 84%;
`

const FavoriteMonsterContainer = styled.div`
  position: absolute;
  top: 54%;
  left: 60%;
  width: 38%;
  text-align: center;
`

const FavoriteMonsterText = styled.div`
  font-weight: bold;
  text-align: center;
`

const HunterRankText = styled.div`
  position: absolute;
  font-weight: bold;
  top: 70.5%;
  left: 37.5%;
  width: 30%;
  text-align: center;
`

const PreviewImage = styled.img`
  position: absolute;
  top: 13%;
  left: 38%;
  width: 100px;
  height: 100px;
  object-fit: contain;
`
